import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { SanityArticleDataWithAuthor } from 'types/sanity';

import { getSanityImageDimensions, urlFor } from '../../helpers/image';
import { getLocalizedUrl } from '../../helpers/url';
import SanityContent from '../SanityContent';
import { Typography } from '../Typography';

type ArticleContentProps = Pick<
  SanityArticleDataWithAuthor,
  'mainImage' | 'alt' | 'title' | 'author' | '_createdAt' | 'body' | 'sections'
>;

export const ArticleContent = ({ data }: { data: ArticleContentProps }) => {
  const imageDimensions = data.mainImage && getSanityImageDimensions(data.mainImage);
  const imageUrl = data.mainImage && urlFor(data.mainImage);
  const router = useRouter();

  return (
    <main className="mb-10 space-y-10">
      <article className="lg:rounded-xl bg-white h-full overflow-hidden">
        <div className="relative h-[240px] lg:h-[480px]">
          {imageUrl && imageDimensions && (
            <Image
              src={imageUrl.url()}
              alt={data.alt}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              sizes="(max-width: 800px) 100vw, 800px"
            />
          )}
        </div>
        <div className="p-4 lg:px-20 lg:py-10">
          <div className="mb-4 md:mb-6">
            <Typography as="h1" size="2xl" color="blue">
              {data.title}
            </Typography>
          </div>
          <SanityContent blocks={data.body} />
        </div>
      </article>
      {data.sections && <SanityContent blocks={data.sections} />}

      <div className="px-6 space-x-2">
        {data.author && (
          <Link
            href={getLocalizedUrl('/articles/author/[name]', router.locale, {
              name: data.author?.category === 'guest' ? 'guest' : data.author.slug?.current,
            })}
            locale={router.locale}
            passHref
          >
            <Typography as="a" size="sm">
              {data.author.name}
            </Typography>
          </Link>
        )}
        <Typography size="xs">{format(new Date(data._createdAt), 'dd/MM/yyyy')}</Typography>
      </div>
    </main>
  );
};
